import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, User, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { cart, user, setAuthOpen, logout } = useApp();
  const { settings } = useSiteSettings();
  const nav = useNavigate();
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 w-full border-b border-white/10 backdrop-blur-xl bg-black/60"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt={settings.brand_name} className="h-10 w-10 rounded-md object-cover border border-white/10"/>
          ) : (
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center border border-blue-400/40">
              <span className="font-display font-bold text-white text-lg">{(settings.brand_name || "P").slice(0, 1)}</span>
            </div>
          )}
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display font-semibold text-lg tracking-tight">{settings.brand_name}</span>
            <span className="mono-label text-white/50">{settings.brand_tagline}</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm">
          <Link to="/shop" data-testid="nav-shop" className="text-white/70 hover:text-white transition-colors">Shop</Link>
          <Link to="/shop?brand=Apple" data-testid="nav-apple" className="text-white/70 hover:text-white transition-colors">iPhone</Link>
          <Link to="/shop?brand=Samsung" data-testid="nav-samsung" className="text-white/70 hover:text-white transition-colors">Samsung</Link>
          <Link to="/about" data-testid="nav-about" className="text-white/70 hover:text-white transition-colors">About</Link>
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost" size="icon"
            data-testid="nav-search"
            onClick={() => nav("/shop")}
            className="text-white/70 hover:text-white"
          >
            <Search className="h-5 w-5" />
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="nav-user" className="text-white/70 hover:text-white">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white">
                <div className="px-2 py-1.5 text-xs text-white/50 font-mono truncate max-w-[220px]">
                  {user.name || user.email || user.phone}
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/account")} className="cursor-pointer">
                  My orders
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")} className="cursor-pointer">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost" size="sm"
              data-testid="nav-login"
              onClick={() => setAuthOpen(true)}
              className="text-white/80 hover:text-white"
            >
              Sign in
            </Button>
          )}

          <Button
            variant="ghost" size="icon"
            data-testid="nav-cart"
            onClick={() => nav("/cart")}
            className="text-white/70 hover:text-white relative"
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-yellow-500 text-black text-[10px] font-bold flex items-center justify-center"
              >
                {cartCount}
              </span>
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
